import { useState, useCallback } from "react";
import { type ValidationMessageGroup } from "datasets/pages/tables/types/interfaces/ingestion-job";
import { CheckCircle, AlertCircle, XCircle, Loader2, CircleDashed } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Badge } from "~/components/ui/badge";
import { getS3PresignedDownloadUrl } from "~/lib/downloadS3File";
import { cn } from "~/lib/utils";
import { stripS3Prefix } from "./utils/jobParsers";

export const SourceFileLabel = ({
  fileName,
  label = "Selected file:",
  placeholder = "No file selected",
}: {
  fileName: string | null;
  label?: string;
  placeholder?: string;
}) => {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <span>{label}</span>
      {fileName ? (
        <span className="font-medium">{fileName}</span>
      ) : (
        <span className="italic text-muted-foreground">{placeholder}</span>
      )}
    </div>
  );
};

export type ValidationStatus = "passed" | "warning" | "running" | "failed" | "uploading";

const statusConfig: Record<
  ValidationStatus,
  {
    variant: "success" | "warning" | "default" | "destructive" | "secondary";
    icon: React.ReactNode;
    label: string;
  }
> = {
  passed: {
    variant: "success",
    icon: <CheckCircle className="w-3 h-3" />,
    label: "PASSED",
  },
  warning: {
    variant: "warning",
    icon: <AlertCircle className="w-3 h-3" />,
    label: "WARNING",
  },
  running: {
    variant: "default",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "RUNNING",
  },
  failed: {
    variant: "destructive",
    icon: <XCircle className="w-3 h-3" />,
    label: "FAILED",
  },
  uploading: {
    variant: "secondary",
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
    label: "UPLOADING",
  },
};

export const StatusBadge = ({ status }: { status: ValidationStatus }) => {
  const config = statusConfig[status];
  return (
    <Badge variant={config.variant}>
      {config.icon}
      {config.label}
    </Badge>
  );
};
const checklistIcons: Record<ValidationStatus, React.ReactNode> = {
  passed: <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />,
  warning: <AlertCircle className="w-4 h-4 text-yellow-600 shrink-0" />,
  running: <CircleDashed className="w-4 h-4 text-gray-400 shrink-0" />,
  failed: <XCircle className="w-4 h-4 text-red-600 shrink-0" />,
  uploading: <CircleDashed className="w-4 h-4 text-gray-400 shrink-0" />,
};

export interface ValidationJobCardProps {
  title: string;
  description: string;
  result: string;
  status: ValidationStatus;
  details?: string;
  messages?: string[];
  messageGroups?: ValidationMessageGroup[];
}

const messageStyles = {
  warning: {
    className: "bg-yellow-50 border-yellow-200 text-yellow-700",
    Icon: AlertCircle,
  },
  failed: {
    className: "bg-red-50 border-red-200 text-red-700",
    Icon: XCircle,
  },
} as const;

export const ValidationJobCard = ({
  title,
  description,
  result,
  status,
  details,
  messages,
  messageGroups,
}: ValidationJobCardProps) => {
  const style = status === "warning" || status === "failed" ? messageStyles[status] : null;

  return (
    <AccordionItem value={title}>
      <AccordionTrigger className="py-2.5 px-3 items-center">
        <div className="flex items-center gap-2.5">
          {checklistIcons[status]}
          <span className="text-sm">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pl-9 pr-3">
        <p className="text-sm text-gray-600 mb-1">{description}</p>
        <p className="text-sm text-gray-700">{result}</p>
        {details && <p className="text-sm text-gray-600 mt-2">{details}</p>}

        {style && messageGroups && messageGroups.length > 0 && (
          <div className={cn("mt-2 rounded-lg border text-sm", style.className)}>
            <Accordion type="multiple">
              {messageGroups.map((group) => (
                <AccordionItem key={group.reason} value={group.reason} className="border-b-0">
                  <AccordionTrigger className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <style.Icon className="w-3.5 h-3.5 shrink-0" />
                      <span>{group.label}</span>
                      <span className="text-xs opacity-70">
                        ({group.columns.length} {group.columns.length === 1 ? "column" : "columns"})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3">
                    <p className="text-xs opacity-80 mb-1.5">{group.description}</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {group.columns.map((col) => (
                        <li key={col} className="font-mono text-xs">
                          {col}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {style && !messageGroups?.length && messages && messages.length > 0 && (
          <ul className={cn("mt-2 p-2 rounded-lg border text-sm space-y-0.5", style.className)}>
            {messages.map((msg, idx) => (
              <li key={idx} className="flex items-start gap-1.5">
                <style.Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{msg}</span>
              </li>
            ))}
          </ul>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export const ValidationJobCardLoading = ({ title }: { title: string }) => (
  <AccordionItem value={`loading-${title}`}>
    <AccordionTrigger hideDefaultIcon className="py-2.5 px-3 items-center cursor-default">
      <div className="flex items-center gap-2.5">
        <CircleDashed className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="text-sm">{title}</span>
      </div>
    </AccordionTrigger>
  </AccordionItem>
);

// ---------------------------------------------------------------------------
// Validation Error File Downloads
// ---------------------------------------------------------------------------

interface DownloadLinkProps {
  label: string;
  s3Uri: string;
}

const DownloadLink = ({ label, s3Uri }: DownloadLinkProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileName = stripS3Prefix(s3Uri)?.split("/").pop() ?? null;

  const handleDownload = useCallback(async () => {
    const objectKey = stripS3Prefix(s3Uri);
    if (!objectKey) {
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      const presignedUrl = await getS3PresignedDownloadUrl(objectKey);
      const anchor = document.createElement("a");
      anchor.href = presignedUrl;
      anchor.download = fileName ?? "download";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      setError("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  }, [s3Uri, fileName]);

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDownloading}
        className="text-sm underline-offset-2 hover:underline disabled:opacity-50 cursor-pointer"
      >
        {isDownloading && <Loader2 className="inline w-3 h-3 animate-spin mr-1" />}
        {label}
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </span>
  );
};

export interface ValidationErrorDownloadsProps {
  invalidRowsFilePath?: string;
  rowDetailsFilePath?: string;
}

export const ValidationErrorDownloads = ({
  invalidRowsFilePath,
  rowDetailsFilePath,
}: ValidationErrorDownloadsProps) => {
  if (!invalidRowsFilePath && !rowDetailsFilePath) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
      {invalidRowsFilePath && (
        <DownloadLink label="Download invalid rows (.csv)" s3Uri={invalidRowsFilePath} />
      )}
      {rowDetailsFilePath && (
        <DownloadLink label="Download error details (.json)" s3Uri={rowDetailsFilePath} />
      )}
    </div>
  );
};
